'use client';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { api } from '@/lib/trpc/react';
import * as Sentry from '@sentry/nextjs';
import { skipToken } from '@tanstack/react-query';
import type { RouterOutputs } from '@unriddle/api';
import { getUserPlanDetails } from '@unriddle/validators/plan';

import { AuthRedirect } from '@/components/AuthRedirect';
import { CheckoutCompleteHandler } from '@/components/CheckoutCompleteHandler';
import { DeviceChecker } from '@/components/DeviceChecker';
import { useImpersonation } from '@/hooks/useImpersonation';
import { useSession } from 'next-auth/react';
import { usePostHog } from 'posthog-js/react';
import type React from 'react';
import {
  Suspense,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

declare global {
  interface Window {
    gtag: (command: string, action: string, params: object) => void;
  }
}

type AuthContext = {
  user?: RouterOutputs['user']['byEmail'] | null;
  userLoading: boolean;
  userPlanDetails: ReturnType<typeof getUserPlanDetails> | null;
  isLoggedIn: boolean;
};

const AuthContext = createContext<AuthContext>({} as AuthContext);

type AuthContextProviderProps = {
  children: React.ReactNode;
  isLandingPage?: boolean;
};

export const AuthContextProvider = ({
  children,
  isLandingPage,
}: AuthContextProviderProps) => {
  const posthog = usePostHog();
  const { track } = useAnalytics();
  const [checkoutComplete, setCheckoutComplete] = useState(false);
  const { checkAdmin } = useImpersonation();
  const session = useSession();
  const analyticsInitialized = useRef<string | null>(null);

  useEffect(() => {
    if (session.data?.user.email) {
      localStorage.setItem('anara_user_email', session.data.user.email);
    }
  }, [session.data?.user.email]);

  const { data: user, isLoading: userLoading } = api.user.byEmail.useQuery(
    session.data?.user.email ?? skipToken,
    {
      enabled: session.status === 'authenticated',
      refetchInterval: (query) => {
        if (!query.state.data || !checkoutComplete) return false;

        const userPlanDetails = getUserPlanDetails(query.state.data);
        if (userPlanDetails?.status !== 'ACTIVE') {
          return 5000;
        }
        return false;
      },
      refetchOnWindowFocus: true,
      staleTime: 0,
    },
  );

  const userPlanDetails = user ? getUserPlanDetails(user) : null;

  useEffect(() => {
    const userAgent = window.navigator.userAgent;
    const url = window.location.href;

    if (user) return;

    if (
      userAgent.includes('Mobile') &&
      (userAgent.includes('iPhone') || userAgent.includes('iPad')) &&
      userAgent.includes('Instagram')
    ) {
      window.location.href = `x-safari-${url}`;
      return;
    }
  }, [user]);

  const initAnalytics = useCallback(
    async (user: NonNullable<RouterOutputs['user']['byEmail']>) => {
      Sentry.setUser({ id: session.data?.user.id, email: user.email });

      const admin = await checkAdmin(user.email);

      if (admin) return;

      const planDetails = getUserPlanDetails(user);

      if (!planDetails) return;

      const profileUpdate: Record<string, string | number | boolean> = {
        $email: user.email,
        $plan: planDetails.status !== 'ACTIVE' ? 'FREE' : planDetails.planName,
        $billing_interval: planDetails.billingInterval,
      };

      // Conditionally add display name if it exists
      if (user.displayName) {
        profileUpdate.$name = user.displayName; // Use $name for reserved property name
      }

      if (!posthog._isIdentified() || posthog.get_distinct_id() !== user.id) {
        posthog.identify(user.id, profileUpdate);
        track('Sign In');
      }
    },
    [checkAdmin, posthog, track, session.data?.user.id],
  );

  useEffect(() => {
    if (!user || analyticsInitialized.current === user.id) return;

    initAnalytics(user);
    analyticsInitialized.current = user.id;
  }, [user, initAnalytics]);

  const handleCheckoutComplete = useCallback((isComplete: boolean) => {
    setCheckoutComplete(isComplete);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        userLoading,
        userPlanDetails,
        isLoggedIn: session.status === 'authenticated',
      }}
    >
      <Suspense fallback={null}>
        <AuthRedirect isLandingPage={isLandingPage} />
        <DeviceChecker />
        <CheckoutCompleteHandler onCheckoutComplete={handleCheckoutComplete} />
      </Suspense>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};
